/**
 * Collection Scroll Tracker
 * Tracks product visibility on collection pages and fires view_item_list events
 * for products that come into view via scrolling
 */

class CollectionScrollTracker {
  constructor() {
    this.trackedProducts = new Set();
    this.pendingProductsByRow = new Map(); // Map of rowTop -> array of products
    this.observer = null;
    this.collectionId = null;
    this.collectionTitle = null;
    this.rowFlushTimeout = null;
    
    this.init();
  }

  init() {
    // Only run on collection pages
    const mainElement = document.querySelector('main[data-template]');
    const template = mainElement?.getAttribute('data-template');
    
    if (!template || !template.includes('collection')) {
      return;
    }

    // Get collection info from page data
    if (window.pageData) {
      this.collectionTitle = window.pageData.pagename;
    }

    // Mark initially visible products as tracked (from initial dataLayer push)
    this.markInitialProductsAsTracked();

    // Set up intersection observer for remaining products
    this.setupObserver();
  }

  markInitialProductsAsTracked() {
    // First, check dataLayer for any products already tracked in view_item_list events
    this.markProductsFromDataLayer();
    
    // Then mark products currently in viewport
    const productTiles = document.querySelectorAll('.product-grid__item');
    
    productTiles.forEach(tile => {
      const productId = tile.dataset.productId;
      if (productId && this.isInViewport(tile)) {
        this.trackedProducts.add(productId);
      }
    });
  }

  markProductsFromDataLayer() {
    // Check if dataLayer exists
    if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
      return;
    }

    // Find all view_item_list events and extract product IDs
    window.dataLayer.forEach(event => {
      if (event.event === 'view_item_list' && event.ecommerce && event.ecommerce.items) {
        event.ecommerce.items.forEach(item => {
          if (item.item_id) {
            this.trackedProducts.add(String(item.item_id));
          }
        });
      }
    });
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  setupObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.handleProductVisible(entry.target);
        }
      });
    }, options);

    // Observe all product tiles
    const productTiles = document.querySelectorAll('.product-grid__item');
    productTiles.forEach(tile => {
      this.observer.observe(tile);
    });

    // Set up scroll end detection to flush remaining products
    this.setupScrollEndDetection();
  }

  setupScrollEndDetection() {
    let scrollTimeout;
    
    window.addEventListener('scroll', () => {
      // Clear existing timeout
      clearTimeout(scrollTimeout);
      
      // Set new timeout to flush pending rows after scrolling stops
      scrollTimeout = setTimeout(() => {
        this.flushPendingRows();
      }, 500);
    }, { passive: true });
  }

  handleProductVisible(productTile) {
    const productId = productTile.dataset.productId;
    
    // Skip if already tracked
    if (!productId || this.trackedProducts.has(productId)) {
      return;
    }

    // Mark as tracked
    this.trackedProducts.add(productId);

    // Get product data from the custom-product-tile
    const customProductTile = productTile.querySelector('custom-product-tile');
    if (!customProductTile) {
      return;
    }

    const trackingScript = customProductTile.querySelector('script[product-tracking-data]');
    if (!trackingScript) {
      return;
    }

    try {
      const productData = JSON.parse(trackingScript.textContent);
      
      // Build item object for dataLayer
      const item = this.buildItemObject(productData);
      
      // Get the row position (top offset of the product tile)
      const rect = productTile.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const rowTop = Math.round((rect.top + scrollTop) / 10) * 10; // Round to nearest 10px to group rows
      
      // Add to pending products for this row
      if (!this.pendingProductsByRow.has(rowTop)) {
        this.pendingProductsByRow.set(rowTop, []);
      }
      this.pendingProductsByRow.get(rowTop).push(item);
      
      // Clear any existing timeout
      if (this.rowFlushTimeout) {
        clearTimeout(this.rowFlushTimeout);
      }
      
      // Set timeout to flush rows after products stop coming into view
      this.rowFlushTimeout = setTimeout(() => {
        this.flushPendingRows();
      }, 200);
    } catch (e) {
      console.error('Failed to parse product tracking data:', e);
    }
  }

  flushPendingRows() {
    if (this.pendingProductsByRow.size === 0) {
      return;
    }

    // Process each row separately
    for (const [rowTop, products] of this.pendingProductsByRow.entries()) {
      if (products.length > 0) {
        this.pushViewItemList(products);
      }
    }
    
    // Clear all pending rows
    this.pendingProductsByRow.clear();
  }

  buildItemObject(productData) {
    const metafields = productData.metafields || {};
    const price = (productData.price || 0) / 100.0;
    const compareAtPrice = (productData.compare_at_price || productData.price || 0) / 100.0;
    const msrp = compareAtPrice > price ? compareAtPrice : price;
    const msrpMarkdown = compareAtPrice > price ? parseFloat((compareAtPrice - price).toFixed(2)) : 0;

    return {
      item_id: String(productData.id),
      item_name: productData.title,
      item_variant: String(productData.variant_id),
      item_brand: productData.vendor,
      price: price,
      msrp: msrp,
      msrp_markdown: msrpMarkdown,
      item_category: metafields.gender || '',
      item_category2: metafields.category || '',
      item_category3: metafields.refinement_color || '',
      item_category4: metafields.class || '',
      item_category5: metafields.material_short_id || '',
      item_type: metafields.category || '',
      item_color: metafields.refinement_color || '',
      item_gender: metafields.gender || '',
      item_size: productData.variant_title || '',
      material: metafields.material_description || '',
      quantity: 1,
      index: parseInt(productData.index) || 0,
      inventory: productData.inventory || 0,
      location: window.Shopify?.country?.toLowerCase() || 'us',
      url: productData.url || '',
      image: productData.image || '',
      affiliation: 'online'
    };
  }

  pushViewItemList(items) {
    if (!window.dataLayer || !window.pageData) {
      return;
    }

    // Get collection ID from URL or page
    const collectionIdMatch = window.location.pathname.match(/\/collections\/[^\/]+/);
    const itemListId = collectionIdMatch ? collectionIdMatch[0] : 'collection';

    window.dataLayer.push({
      event: 'view_item_list',
      event_id: 'evt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
      event_time: new Date().toISOString(),
      ecommerce: {
        currency: window.ecommerce?.currency || 'USD',
        item_list_id: itemListId,
        item_list_name: this.collectionTitle || 'Collection',
        items: items,
        current_page: Number(document.querySelector('[data-page]')?.getAttribute('data-page') ?? 1)
      },
      user_data: window.userData || {},
      page_data: window.pageData || {},
      marketing_data: {}
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.trackedProducts.clear();
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionScrollTracker();
  });
} else {
  new CollectionScrollTracker();
}
